import React from 'react';

// Where the analyst is in the feed graph, and how they got there.
//
// This used to be an implicit state machine spread across four files and eight slots: a stack, a ref
// mirroring it, two refs following in-flight reads, a third assigned DURING RENDER to pair a result
// with the feed it described, plus the result itself and the unavailable-marker state. Five of those
// are already gone — the response carries its own feed id now, and useFeedReads owns the read flags
// — and what is left is small enough to say out loud.
//
// WHICH FEED IS SHOWING IS NOT IN HERE. It is `result.feedId`, read off the response, and putting a
// copy of it in this reducer would recreate exactly the pairing that used to drift.
//
// THE PATH ENTRY IS PUSHED WHEN THE ROWS LAND, NOT WHEN THE PRESS HAPPENS. Pushing first made a
// second press unsurvivable: until the first read lands, the table on screen is still the parent's,
// so a second press captured the parent again and pushed it under the first member's name. The
// entry was wrong at the moment it was made, and no unwinding could fix it. Deferring removes the
// cause — a superseded read has no entry anywhere to unwind.
//
// The cost is a frame: between the member's rows arriving and the push, the table renders with the
// trail one entry short. That understates the path for an instant; the old order overstated it, and
// an understatement never names a feed above another feed's rows.

const INITIAL = { stack: [], unavailable: null };

const ACTIONS = {
  // A top-level read's rows landed. They answer a query, not a path, so the old path stops
  // describing what is on screen — now, rather than when the query was submitted.
  PATH_REPLACED: 'PATH_REPLACED',
  // A descent's rows landed. The entry is the feed we came FROM.
  DESCENDED: 'DESCENDED',
  // Stepping back up. The caller has already read the entry it is restoring.
  POPPED_TO: 'POPPED_TO',
  // A marker was pressed whose feed could not be resolved.
  MARKER_UNAVAILABLE: 'MARKER_UNAVAILABLE',
  UNAVAILABLE_DISMISSED: 'UNAVAILABLE_DISMISSED',
  // Back to first load.
  RESET: 'RESET',
};

const reducer = (state, action) => {
  switch (action.type) {
    case ACTIONS.PATH_REPLACED:
      return state.stack.length === 0 ? state : { ...state, stack: [] };
    // Appends to the CURRENT stack rather than to the one the caller's render captured, which is
    // what keeps two overlapping presses from both claiming depth 0. That is the whole reason the
    // stack used to be mirrored in a ref; a reducer gets it for free.
    case ACTIONS.DESCENDED:
      return { ...state, stack: [...state.stack, action.entry] };
    case ACTIONS.POPPED_TO:
      return { ...state, stack: state.stack.slice(0, action.depth) };
    case ACTIONS.MARKER_UNAVAILABLE:
      return { ...state, unavailable: action.marker };
    case ACTIONS.UNAVAILABLE_DISMISSED:
      return state.unavailable === null ? state : { ...state, unavailable: null };
    case ACTIONS.RESET:
      return INITIAL;
    default:
      return state;
  }
};

export const useFeedNavigation = () => {
  const [state, dispatch] = React.useReducer(reducer, INITIAL);

  // Reads the entry from the CURRENT render's state and returns it synchronously, because the caller
  // restores a stored result with it. No ref is needed: a pop comes from a click handler, so the
  // render that handles it is looking at the stack that click was made against.
  const popTo = React.useCallback(
    (depth) => {
      if (depth < 0 || depth >= state.stack.length) return null;
      dispatch({ type: ACTIONS.POPPED_TO, depth });
      return state.stack[depth];
    },
    [state.stack]
  );

  return {
    stack: state.stack,
    depth: state.stack.length,
    unavailable: state.unavailable,
    popTo,
    replacePath: React.useCallback(() => dispatch({ type: ACTIONS.PATH_REPLACED }), []),
    descendedFrom: React.useCallback((entry) => dispatch({ type: ACTIONS.DESCENDED, entry }), []),
    markUnavailable: React.useCallback((marker) => dispatch({ type: ACTIONS.MARKER_UNAVAILABLE, marker }), []),
    dismissUnavailable: React.useCallback(() => dispatch({ type: ACTIONS.UNAVAILABLE_DISMISSED }), []),
    reset: React.useCallback(() => dispatch({ type: ACTIONS.RESET }), []),
  };
};
